import { lazy, Suspense } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';
// Config
import ConfigRootPage from 'src/pages/dashboard/config/root';

import { LoadingScreen } from 'src/components/loading-screen';

// ---------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
// PRODUCT
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
const ServiceListPage = lazy(() => import('src/pages/dashboard/service/list'));
const AppointmentCalendarPage = lazy(() => import('src/pages/dashboard/appointment/calendar'));
const AppointmentDashboardPage = lazy(() => import('src/pages/dashboard/appointment/dashboard'));
const AppointmentSettingsPage = lazy(() => import('src/pages/dashboard/appointment/settings'));
// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
const UserDetailPage = lazy(() => import('src/pages/dashboard/user/detail'));
// BLOG
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// JOB
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// TOUR
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
// PDV
const PdvRootPage = lazy(() => import('src/pages/dashboard/pdv/root'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
// GIFTBACK
const GiftbackListPage = lazy(() => import('src/pages/dashboard/giftback/list'));
const GiftbackCreatePage = lazy(() => import('src/pages/dashboard/giftback/new'));
const GiftbackDetailsPage = lazy(() => import('src/pages/dashboard/giftback/details'));
// LOYALTY
const LoyaltyRootPage = lazy(() => import('src/pages/dashboard/loyalty/root'));
// COUPON
const CouponListPage = lazy(() => import('src/pages/dashboard/coupon/list'));
const CouponCreatePage = lazy(() => import('src/pages/dashboard/coupon/new'));
// CUSTOMER PAGE
const CustomerListPage = lazy(() => import('src/pages/dashboard/customer/list'));
const CustomerCreatePage = lazy(() => import('src/pages/dashboard/customer/new'));
const CustomerEditPage = lazy(() => import('src/pages/dashboard/customer/edit'));
const CustomerDetailPage = lazy(() => import('src/pages/dashboard/customer/detail'));

// PURCHASE PAGE
const PurchaseListPage = lazy(() => import('src/pages/dashboard/purchase/list'));
const PurchaseDetailPage = lazy(() => import('src/pages/dashboard/purchase/details'));
const PurchaseInvoiceDetailsPage = lazy(() => import('src/pages/dashboard/purchase/invoice'));

// TOTEM PAGE
const TotemRootPage = lazy(() => import('src/pages/dashboard/totem/root'));

// ANALYTICS PAGES
const DashboardCustomerAnalytics = lazy(() => import('src/pages/dashboard/analytics/customer'));
const DashboardRfmAnalytics = lazy(() => import('src/pages/dashboard/analytics/rfm'));
const DashboardRetentionAnalytics = lazy(() => import('src/pages/dashboard/analytics/retention'));
const DashboardRetentionPreview = lazy(
  () => import('src/pages/dashboard/analytics/retention-preview')
);
const DashboardGiftbackAnalytics = lazy(() => import('src/pages/dashboard/analytics/giftbacks'));
const DashboardSalesAnalytics = lazy(() => import('src/pages/dashboard/analytics/sales'));

// TEMPLATE CAMPAIGN PAGE
const CampaignListPage = lazy(() => import('src/pages/dashboard/campaign/list'));
const CampaignCreatePage = lazy(() => import('src/pages/dashboard/campaign/new'));
const CampaignEditPage = lazy(() => import('src/pages/dashboard/campaign/edit'));
const CampaignDetailPage = lazy(() => import('src/pages/dashboard/campaign/details'));

// JOURNEYS PAGE
const JourneyListPage = lazy(() => import('src/pages/dashboard/journeys/list'));
const PlanningPage = lazy(() => import('src/pages/dashboard/planning/root'));
const CreateSendPage = lazy(() => import('src/pages/dashboard/mass-campaign/send'));
const CommunitiesPage = lazy(() => import('src/pages/dashboard/communities/root'));
const JourneyTemplatesPage = lazy(() => import('src/pages/dashboard/journeys/templates'));
const JourneyEditPage = lazy(() => import('src/pages/dashboard/journeys/edit'));
const JourneyExecutionPage = lazy(() => import('src/pages/dashboard/journeys/execution'));

// MASS CAMPAIGN PAGE
const MassCampaignListPage = lazy(() => import('src/pages/dashboard/mass-campaign/list'));
const MassCampaignCreatePage = lazy(() => import('src/pages/dashboard/mass-campaign/new'));
const MassCampaignEditView = lazy(
  () => import('src/sections/mass-campaign/view/mass-campaign-edit-view')
);

// Wrapper component to handle the id parameter
const MassCampaignEditPage = () => {
  const { id } = useParams();
  return <MassCampaignEditView id={id || ''} />;
};
const MassCampaignDetailPage = lazy(() => import('src/pages/dashboard/mass-campaign/details'));
// ----------------------------------------------------------------------

export const dashboardRoutes = [
  ...(import.meta.env.DEV
    ? [
        {
          path: 'preview/retention',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <DashboardRetentionPreview />
            </Suspense>
          ),
        },
      ]
    : []),
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      {
        path: 'user',
        children: [
          { element: <UserListPage />, index: true },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'list', element: <UserListPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: ':id/detail', element: <UserDetailPage /> },
          { path: 'account', element: <UserAccountPage /> },
        ],
      },
      {
        path: 'product',
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      { path: 'service', element: <ServiceListPage /> },
      {
        path: 'communities',
        children: [
          { element: <CommunitiesPage />, index: true },
          { path: 'new', element: <CommunitiesPage /> },
          { path: ':id', element: <CommunitiesPage /> },
        ],
      },
      {
        path: 'agenda',
        children: [
          { element: <AppointmentCalendarPage />, index: true },
          { path: 'dashboard', element: <AppointmentDashboardPage /> },
          { path: 'configuracoes', element: <AppointmentSettingsPage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },
      {
        path: 'invoice',
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      {
        path: 'customer',
        children: [
          { element: <CustomerListPage />, index: true },
          { path: 'list', element: <CustomerListPage /> },
          { path: 'new', element: <CustomerCreatePage /> },
          { path: ':id/edit', element: <CustomerEditPage /> },
          { path: ':id/detail', element: <CustomerDetailPage /> },
        ],
      },
      {
        path: 'giftback',
        children: [
          { element: <GiftbackListPage />, index: true },
          { path: 'new', element: <GiftbackCreatePage /> },
          { path: ':id/detail', element: <GiftbackDetailsPage /> },
        ],
      },
      { path: 'loyalty', element: <LoyaltyRootPage /> },
      {
        path: 'coupon',
        children: [
          { element: <CouponListPage />, index: true },
          { path: 'new', element: <CouponCreatePage /> },
        ],
      },
      {
        path: 'purchase',
        children: [
          { element: <PurchaseListPage />, index: true },
          { path: ':id/detail', element: <PurchaseDetailPage /> },
          { path: ':id/invoice', element: <PurchaseInvoiceDetailsPage /> },
        ],
      },
      {
        path: 'analytics',
        children: [
          { element: <DashboardCustomerAnalytics />, index: true },
          { path: 'customer', element: <DashboardCustomerAnalytics /> },
          { path: 'rfm', element: <DashboardRfmAnalytics /> },
          { path: 'retention', element: <DashboardRetentionAnalytics /> },
          { path: 'giftback', element: <DashboardGiftbackAnalytics /> },
          { path: 'sales', element: <DashboardSalesAnalytics /> },
        ],
      },
      {
        path: 'totem',
        element: <TotemRootPage />,
      },
      { path: 'pdv', element: <PdvRootPage /> },
      {
        path: 'message',
        children: [
          { element: <CampaignListPage />, index: true },
          { path: 'new', element: <CampaignCreatePage /> },
          { path: ':id/edit', element: <CampaignEditPage /> },
          { path: ':id/detail', element: <CampaignDetailPage /> },
        ],
      },
      {
        path: 'campaign',
        children: [
          { element: <MassCampaignListPage />, index: true },
          { path: 'send', element: <CreateSendPage /> },
          { path: 'new', element: <MassCampaignCreatePage /> },
          { path: ':id/edit', element: <MassCampaignEditPage /> },
          { path: ':id/detail', element: <MassCampaignDetailPage /> },
        ],
      },
      {
        path: 'jornadas',
        children: [
          { element: <JourneyListPage />, index: true },
          { path: 'templates', element: <JourneyTemplatesPage /> },
          { path: ':id/edit', element: <JourneyEditPage /> },
          { path: 'executions/:id', element: <JourneyExecutionPage /> },
        ],
      },
      { path: 'config', element: <ConfigRootPage /> },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'planning', element: <PlanningPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
    ],
  },
  {
    path: 'totem',
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </AuthGuard>
    ),
    children: [{ path: '', element: <TotemRootPage /> }],
  },
];
